import { useCallback, useEffect, useState, useMemo } from 'react'
import { useGetTreeById } from '../../../reducers/trees_hooks'
import {
  useSetAtom,
  useAtomValue,
  useAtom,
} from 'infrastructure/state/jotai.wrapper'
import { INode } from 'shared/types/tree'
import {
  standardIdsAtom,
  standardIdMapAtom,
  idsNotFoundAtom,
  nodesFromTreeAtom,
  treeNodeIdToParentIdMapAtom,
  importedSheetDataAtom,
  sheetImportErrorsAtom,
  syncResultsAtom,
  syncErrorsAtom,
  refreshCounterAtom,
  cascadeFromAtom,
  cascadeToAtom,
  cascadeTitleAtom,
  cascadeModeAtom,
  syncDifferencesAtom,
} from './atoms'
import {
  SkillsListDifference,
  ContentServiceSyncRequest,
  SyncResult,
  CascadeNodeType,
} from './types'
import { calculateSyncSummaryRows } from './diff_utils'
import { ContentServiceFlowResults } from '../../contentService/types'
import {
  extractStandardIdsFromTree,
  fetchStandardsByIds,
  extractSheetNodesFromTree,
  isAuthError,
  startSyncStandardsFlow,
  calculateSheetNodesSummary,
  createSheetSummaryRows,
  serializeImportedSheetData,
} from './utility'
import { importStandardsFromSheet } from './import_utils'
import {
  extractSkillsListTitles,
  CASCADE_NODE_TYPES,
  detectCascadeOrder,
  isToTypeValidForCascade,
} from './cascade_utils'
import { exportTreeStandardsToSheet } from './export_utils'
import { useNotify } from 'shared/ui/Notify'
import { googleUserAtom } from 'infrastructure/googleDrive'
import { KiddomStandard } from 'components/standards/types'

/**
 * Hook to scan tree and extract standard IDs, then fetch standards data.
 * Automatically updates standardIdsAtom and standardIdMapAtom.
 */
export function useScanAndFetchStandards(treeId: string, versionId: string) {
  const { tree } = useGetTreeById({ treeId, versionId })
  const setStandardIds = useSetAtom(standardIdsAtom)
  const setStandardIdMap = useSetAtom(standardIdMapAtom)
  const setIdsNotFound = useSetAtom(idsNotFoundAtom)
  const setNodesFromTree = useSetAtom(nodesFromTreeAtom)
  const setTreeNodeIdToParentIdMap = useSetAtom(treeNodeIdToParentIdMapAtom)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const notify = useNotify()

  const scanAndFetch = useCallback(async () => {
    if (!tree) {
      setError('No tree available to scan')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Step 1: Extract standard IDs from tree (returns Set)
      const standardIds = extractStandardIdsFromTree(tree)
      setStandardIds(standardIds)

      if (standardIds.size === 0) {
        setStandardIdMap({})
        setIdsNotFound(new Set<number>())
        setNodesFromTree([])
        setLoading(false)
        return
      }

      // Step 2: Fetch standards from API
      const standardMap = await fetchStandardsByIds(standardIds)
      setStandardIdMap(standardMap)

      // Step 3: Calculate which IDs were not found
      const foundIds = new Set(Object.keys(standardMap).map(Number))
      const notFoundIds = new Set<number>(
        Array.from(standardIds).filter((id) => !foundIds.has(id)),
      )
      setIdsNotFound(notFoundIds)

      // Step 4: Extract SheetNodes from tree
      const { flatNodes, nodeIdToParentIdMap } = extractSheetNodesFromTree(
        tree,
        standardMap,
        treeId,
        versionId,
      )
      setNodesFromTree(flatNodes)
      setTreeNodeIdToParentIdMap(nodeIdToParentIdMap)
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch standards'
      setError(errorMessage)
      notify(errorMessage, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [
    tree,
    setStandardIds,
    setStandardIdMap,
    setIdsNotFound,
    setNodesFromTree,
    setTreeNodeIdToParentIdMap,
    notify,
  ])

  // Auto-scan when tree changes
  useEffect(() => {
    if (tree) {
      scanAndFetch()
    }
  }, [tree, scanAndFetch])

  return {
    scanAndFetch,
    loading,
    error,
  }
}

/**
 * Hook to import standards from a Google Sheet.
 * Handles the import logic and updates atoms accordingly.
 */
export function useImportStandardsFromSheet(treeId: string, versionId: string) {
  const [loading, setLoading] = useState(false)
  const guser = useAtomValue(googleUserAtom)
  const importedSheetData = useAtomValue(importedSheetDataAtom)
  const setImportedSheetData = useSetAtom(importedSheetDataAtom)
  const setImportErrors = useSetAtom(sheetImportErrorsAtom)
  const resetGoogleAuth = useSetAtom(googleUserAtom)
  const treeNodeIdToParentIdMap = useAtomValue(treeNodeIdToParentIdMapAtom)
  const notify = useNotify()

  const importFromSheet = useCallback(
    async (sheetId: string) => {
      if (!guser.credential?.accessToken) {
        return
      }

      setLoading(true)
      // Clear previous errors and results
      setImportErrors([])
      setImportedSheetData(null)

      try {
        const { nodeList, errors } = await importStandardsFromSheet(
          sheetId.trim(),
          guser.credential.accessToken,
          treeId,
          versionId,
        )

        // Always set errors (even if empty)
        setImportErrors(errors)

        if (errors.length > 0) {
          // Don't set nodes if there are errors
          notify(
            `Import completed with ${errors.length} error(s). Please fix errors before proceeding.`,
            {
              type: 'error',
            },
          )
        } else {
          // Only set nodes if no errors
          // Calculate skillsTypes: node types that contain skills
          const skillsTypesSet = new Set<string>()
          nodeList.forEach((node) => {
            if (node.skillsLists.length > 0 || node.newSkillsLists.length > 0) {
              skillsTypesSet.add(node.type)
            }
          })
          const skillsTypes = Array.from(skillsTypesSet).sort((a, b) =>
            a.localeCompare(b),
          )

          // Calculate skillsTitles: titles of all skills lists from the imported sheet
          // This captures all original titles found in the document, including any additional ones
          const skillsTitles = extractSkillsListTitles(nodeList)

          // Calculate targetTypes: target types (to field types) found in the tree
          // Target types are: module, unit, lesson_plan, activity, assessment, question, document
          const targetTypesSet = new Set<string>()
          nodeList.forEach((node) => {
            if (CASCADE_NODE_TYPES.includes(node.type as CascadeNodeType)) {
              targetTypesSet.add(node.type)
            }
          })
          const targetTypes = Array.from(targetTypesSet).sort((a, b) =>
            a.localeCompare(b),
          )

          // Detect cascade hierarchy order from nodeList
          const cascadeOrder = detectCascadeOrder(nodeList)

          // Consolidate imported nodes data
          setImportedSheetData({
            nodeList,
            skillsTypes,
            skillsTitles,
            targetTypes,
            cascadeOrder,
            treeId,
            versionId,
          })
          notify('Standards imported from Google Sheet successfully', {
            type: 'success',
          })
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to import standards from sheet'

        // Check if this is an authentication error
        if (isAuthError(error)) {
          // Reset auth state to force re-login
          resetGoogleAuth()
          notify('Google authentication expired. Please log in again.', {
            type: 'error',
          })
        } else {
          notify(`Failed to import standards: ${errorMessage}`, {
            type: 'error',
          })
        }
      } finally {
        setLoading(false)
      }
    },
    [
      guser.credential?.accessToken,
      setImportErrors,
      setImportedSheetData,
      treeNodeIdToParentIdMap,
      treeId,
      versionId,
      resetGoogleAuth,
      notify,
    ],
  )

  return {
    importFromSheet,
    loading,
    importedSheetData,
  }
}

/**
 * Hook to export tree standards to a Google Sheet.
 * Handles the export logic including error handling and authentication.
 */
export function useExportTreeStandardsToSheet() {
  const [loading, setLoading] = useState(false)
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null)
  const resetGoogleAuth = useSetAtom(googleUserAtom)
  const notify = useNotify()

  const exportToSheet = useCallback(
    async (
      tree: INode,
      accessToken: string,
      standardIdMap: Record<number, KiddomStandard>,
      filterSkillsNodesOnly: boolean,
    ) => {
      if (!tree || !accessToken) {
        return
      }

      setLoading(true)
      try {
        const id = await exportTreeStandardsToSheet(
          tree,
          accessToken,
          standardIdMap,
          filterSkillsNodesOnly,
        )
        if (id) {
          setSpreadsheetId(id)
          notify('Tree standards exported to Google Sheet successfully', {
            type: 'success',
          })
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to export tree standards'

        // Check if this is an authentication error
        if (isAuthError(error)) {
          // Reset auth state to force re-login
          resetGoogleAuth()
          notify('Google authentication expired. Please log in again.', {
            type: 'error',
          })
        } else {
          notify(`Failed to export tree standards: ${errorMessage}`, {
            type: 'error',
          })
        }
      } finally {
        setLoading(false)
      }
    },
    [resetGoogleAuth, notify],
  )

  return {
    exportToSheet,
    loading,
    spreadsheetId,
  }
}

/**
 * Hook to sync standards using the differences between sheet and tree.
 * Handles starting the sync flow via content service.
 */
export function useSyncStandards(
  treeId: string,
  versionId: string,
  differences: SkillsListDifference[],
) {
  // Convert SkillsListDifference[] to ContentServiceSyncRequest[]
  // Copy all fields except deduplicated, and set tree_skills to empty object (deprecated)
  const contentServiceSyncRequests: ContentServiceSyncRequest[] = useMemo(
    () =>
      differences.map((diff) => ({
        skills_list_node_id: diff.skills_list_node_id,
        skills_list_title: diff.skills_list_title,
        skills_list_parent_node_id: diff.skills_list_parent_node_id,
        skills_list_parent_node_type: diff.skills_list_parent_node_type,
        skills_list_parent_node_title: diff.skills_list_parent_node_title,
        tree_skills: {}, // Deprecated - send empty object
        sheet_skills: diff.sheet_skills,
        new_skills_list: diff.new_skills_list,
        title_change: diff.title_change,
        skills_changed: diff.skills_changed,
      })),
    [differences],
  )

  const setRefreshCounter = useSetAtom(refreshCounterAtom)
  const syncStandards = useCallback(async () => {
    setRefreshCounter((prev) => prev + 1)
    return startSyncStandardsFlow({
      tree_id: treeId,
      version_id: versionId,
      differences: contentServiceSyncRequests,
    })
  }, [treeId, versionId, contentServiceSyncRequests, setRefreshCounter])

  return {
    syncStandards,
  }
}

/**
 * Hook to handle sync completion and update atoms with results and errors.
 * Processes flow results and extracts sync results and errors.
 */
export function useSyncCompletion() {
  const setSyncResults = useSetAtom(syncResultsAtom)
  const setSyncErrors = useSetAtom(syncErrorsAtom)

  return useCallback(
    (flowResults: ContentServiceFlowResults) => {
      const results: SyncResult[] = []
      const errors: string[] = []

      // Process dependant_results
      if (flowResults.data?.dependant_results) {
        flowResults.data.dependant_results.forEach((result) => {
          if (result.result && typeof result.result === 'object') {
            const resultData = result.result as {
              skills_list_id?: string
              standard_ids_created?: number[]
              standard_ids_removed?: number[]
            }
            if (resultData.skills_list_id) {
              results.push({
                skills_list_id: resultData.skills_list_id,
                standard_ids_created: resultData.standard_ids_created || [],
                standard_ids_removed: resultData.standard_ids_removed || [],
              })
            }
          }
        })
      }

      // Process errors
      if (flowResults.errors && flowResults.errors.length > 0) {
        flowResults.errors.forEach((error) => {
          const errorMessage =
            error.error?.value || error.error?.type || 'Unknown error'
          errors.push(errorMessage)
        })
      }

      setSyncResults(results)
      setSyncErrors(errors)
    },
    [setSyncResults, setSyncErrors],
  )
}

/**
 * Hook to manage cascade standards configuration
 * Handles cascade from/to/title selection and extracts skills list titles
 * Returns cascade configuration state and handlers
 */
export function useCascadeStandards(treeId: string, versionId: string) {
  const importedSheetData = useAtomValue(importedSheetDataAtom)
  const nodesFromTree = useAtomValue(nodesFromTreeAtom)
  const [fromType, setFromType] = useAtom(cascadeFromAtom)
  const [toType, setToType] = useAtom(cascadeToAtom)
  const [selectedSkillsListTitle, setSelectedSkillsListTitle] =
    useAtom(cascadeTitleAtom)
  const [mode, setMode] = useAtom(cascadeModeAtom)

  // Use skillsTitles from importedSheetData if available
  const skillsListTitles = useMemo(() => {
    if (importedSheetData?.skillsTitles) {
      return importedSheetData.skillsTitles
    }
    // Fallback: extract from tree nodes if no imported data
    const treeTitles = extractSkillsListTitles(nodesFromTree)
    return treeTitles
  }, [importedSheetData?.skillsTitles, nodesFromTree])

  // Use hardcoded CASCADE_NODE_TYPES order for "From"
  // Types not in targetTypes will be disabled (grayed out)
  // Types without skills will also be disabled
  const fromTypes = useMemo(() => {
    return CASCADE_NODE_TYPES
  }, [])

  // Helper to check if a type is present in the document
  const isTypeInDocument = useCallback(
    (type: string): boolean => {
      return importedSheetData?.targetTypes?.includes(type) ?? false
    },
    [importedSheetData?.targetTypes],
  )

  // Helper to check if a type has skills (for disabling "From" options)
  const hasSkills = useCallback(
    (type: string): boolean => {
      return importedSheetData?.skillsTypes?.includes(type) ?? false
    },
    [importedSheetData?.skillsTypes],
  )

  // Use hardcoded CASCADE_NODE_TYPES order for "To"
  // Types not in targetTypes will be disabled (grayed out)
  const toTypes = useMemo(() => {
    return CASCADE_NODE_TYPES
  }, [])

  // Validity uses static CASCADE_NODE_TYPES order only (see isToTypeValidForCascade)
  const isToTypeValid = useCallback(
    (toTypeValue: string, fromTypeValue: string, modeValue: string): boolean =>
      isToTypeValidForCascade(toTypeValue, fromTypeValue, modeValue),
    [],
  )

  // Clear "To" if it becomes invalid when mode or "From" changes
  useEffect(() => {
    if (
      toType &&
      fromType &&
      mode !== 'Off' &&
      !isToTypeValid(toType, fromType, mode)
    ) {
      setToType('')
    }
  }, [toType, fromType, mode, isToTypeValid, setToType])

  // Handle "To" type change
  const handleToTypeChange = useCallback(
    (type: string) => {
      setToType(type)
    },
    [setToType],
  )

  // Handle "From" type change - clears "To" if it matches
  const handleFromTypeChange = useCallback(
    (type: string) => {
      setFromType(type)
      // Clear "To" if it matches the new "From" type
      if (toType === type) {
        setToType('')
      }
    },
    [setFromType, toType, setToType],
  )

  // Handle skills list title change
  const handleSkillsListTitleChange = useCallback(
    (title: string) => {
      setSelectedSkillsListTitle(title)
    },
    [setSelectedSkillsListTitle],
  )

  // Handle mode change - clear "To" if it becomes invalid
  const handleModeChange = useCallback(
    (newMode: string) => {
      setMode(newMode)
      // Clear "To" if it becomes invalid with the new mode
      if (
        toType &&
        fromType &&
        newMode !== 'Off' &&
        !isToTypeValid(toType, fromType, newMode)
      ) {
        setToType('')
      }
    },
    [setMode, toType, fromType, isToTypeValid, setToType],
  )

  return {
    fromType,
    toType,
    selectedSkillsListTitle,
    skillsListTitles,
    fromTypes,
    toTypes,
    mode,
    isToTypeValid,
    hasSkills,
    isTypeInDocument,
    handleFromTypeChange,
    handleToTypeChange,
    handleSkillsListTitleChange,
    handleModeChange,
  }
}

/**
 * Hook to compute import summary data from imported sheet data
 * Returns summary rows and serializable data for display
 */
export function useImportSummary() {
  const importedSheetData = useAtomValue(importedSheetDataAtom)

  return useMemo(() => {
    if (!importedSheetData?.nodeList) {
      return {
        summaryRows: [],
        serializableData: null,
      }
    }

    const summary = calculateSheetNodesSummary(importedSheetData.nodeList)
    const summaryRows = createSheetSummaryRows(summary)
    const serializableData = serializeImportedSheetData(importedSheetData)

    return {
      summaryRows,
      serializableData,
    }
  }, [importedSheetData])
}

/**
 * Hook to compute sync summary rows from sync differences
 */
export function useSyncSummary(treeId: string, versionId: string) {
  const syncDifferences = useAtomValue(syncDifferencesAtom)

  return useMemo(() => {
    return calculateSyncSummaryRows(syncDifferences, treeId, versionId)
  }, [syncDifferences, treeId, versionId])
}

/**
 * Hook to compute sync results summary from sync results
 * Returns formatted rows and total count for display
 */
export function useSyncResultsSummary() {
  const syncResults = useAtomValue(syncResultsAtom)

  return useMemo(() => {
    const rows = syncResults.map((result) => ({
      cells: [result.skills_list_id, result.standard_ids_created.length],
    }))
    const totalStandardsCreated = syncResults.reduce(
      (sum, result) => sum + result.standard_ids_created.length,
      0,
    )

    return {
      syncResultsRows: rows,
      totalStandardsCreated,
    }
  }, [syncResults])
}

/**
 * Hook to compute tree summary rows from tree nodes
 * Used for export display
 */
export function useTreeSummary() {
  const nodesFromTree = useAtomValue(nodesFromTreeAtom)

  return useMemo(() => {
    const summary = calculateSheetNodesSummary(nodesFromTree)
    return createSheetSummaryRows(summary)
  }, [nodesFromTree])
}
